import React from 'react';
import clsx from 'clsx';
import Typography, { TypographyStyle } from '../Typography/Typography';
import styles from './Anchor.module.scss';

type AnchorProps = React.HTMLProps<HTMLAnchorElement>;

export type AnchorComponentProps = Pick<
    AnchorProps,
    | 'target'
    | 'title'
    | 'className'
    | 'tabIndex'
    | 'href'
    | 'rel'
    | 'onClick'
    | 'role'
    | 'onKeyDown'
> & {
    color?: string;
    noHover?: boolean;
    variant?: TypographyStyle;
    children: React.ReactNode;
};

export const Anchor = ({
    href,
    title,
    rel,
    target,
    color,
    noHover = false,
    variant,
    className,
    onClick,
    children,
    ...rest
}: AnchorComponentProps) => {
    const anchorClass = clsx(
        styles.anchor,
        {
            [styles.noHover]: noHover,
        },
        className,
    );

    const anchorTextClass = clsx(styles.anchorText, { variant });

    const style = color ? ({ '--custom-color': color } as React.CSSProperties) : undefined;

    const typographyVariant = variant ? variant : 'textLink-M';

    const isLink = Boolean(href);
    const computedRole = !isLink ? rest.role ?? 'button' : rest.role;
    const computedTabIndex = !isLink ? rest.tabIndex ?? 0 : rest.tabIndex;

    const handleKeyDown = (e: React.KeyboardEvent<HTMLAnchorElement>) => {
        if (isLink) {
            return;
        }
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick?.(e as unknown as React.MouseEvent<HTMLAnchorElement>);
        }
    };

    return (
        <a
            href={href}
            title={title}
            rel={rel}
            target={target}
            className={anchorClass}
            style={style}
            onClick={onClick}
            role={computedRole}
            tabIndex={computedTabIndex}
            onKeyDown={handleKeyDown}
            {...rest}
        >
            <Typography
                weight="medium"
                variant={typographyVariant}
                className={anchorTextClass}
                as="span"
            >
                {children}
            </Typography>
        </a>
    );
};
